import { Grid, CssBaseline } from "@mui/material";
import CalebAndBrownLogo from "src/icons/cnbDesktop";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import CnbLogoMobile from "src/icons/CnbLogoMobile";
import { LoginStyles } from "src/components/auth/styles/Login.styles";
import GenericMessageModal from "src/components/wallets/Modal/Withdraw/GenericMessageModal";
import { getTravelRuleFragmentOutcome } from "src/utils/travelRuleResultFragment";

/**
 * Landing page for the manual (no-portal) Travel Rule link — Notabene's hosted
 * SafeConnect page sends the customer here via `redirect_uri` once they submit
 * or cancel. Without it the hosted page has nowhere to navigate and the
 * customer is left sitting on the completed form.
 *
 * PUBLIC by design: these customers arrive from an emailed link and are not
 * logged into the portal, so this must render without a session (see the
 * `public: true` route in config.tsx).
 *
 * The default copy deliberately claims nothing about the outcome — the hosted
 * page redirects here on cancel as well as submit, and the authoritative
 * decision arrives later via the Notabene webhook. The one case worth calling
 * out is a component-side validation failure: the backend callback answers 400
 * to those and leaves the link ACTIVE precisely so the customer can retry, so
 * telling them to close the window would strand the transfer. We only switch
 * copy on an explicit `valid: false` recovered from the redirect fragment —
 * anything we can't parse falls back to the neutral message.
 */
export default function TravelRuleDone() {
  const classes = LoginStyles();
  const isMobile = useIsMobile();
  // Captured + stripped at bootstrap (index.tsx) so the PII-bearing fragment
  // never reaches Amplitude, Adora, or browser history.
  const outcome = getTravelRuleFragmentOutcome();
  const failedValidation = outcome?.valid === false;
  // The hosted page also redirects HERE on a fatal error (`#message=
  // {"type":"error",…}`) — expired/invalid link token being the prod case
  // (CT 1256059) — after which the customer has submitted NOTHING. Thanking
  // them would hide the failure, so name it and point at the recovery path.
  const errorIdentifier = outcome?.errorIdentifier;
  const linkExpired = errorIdentifier === "LINK_EXPIRED" || errorIdentifier === "TOKEN_INVALID";
  const message = linkExpired
    ? "This secure link has expired, so nothing was submitted. Please ask your broker to send you a fresh link — or contact us at support@calebandbrown.com."
    : errorIdentifier
      ? "Something went wrong opening this secure link, so nothing was submitted. Please reopen the link from your email and try again — if it keeps failing, contact us at support@calebandbrown.com."
      : failedValidation
        ? "We couldn't verify those details. Please reopen the link from your email and try again — if it keeps failing, contact us at support@calebandbrown.com."
        : "Thanks — you can now close this window. If anything further is needed for this transfer, our team will be in touch.";

  return (
    <div className={classes.container}>
      <CssBaseline />
      <Grid container className={classes.logoBox}>
        {isMobile ? <CnbLogoMobile height={"200px"} /> : <CalebAndBrownLogo />}
      </Grid>
      {/* `GenericMessageModal`'s `halfSize` prop is declared but never applied,
          so the copy would otherwise run the full viewport width on desktop.
          Constrain it here rather than fixing the shared component, which
          several other pages already render at full width. */}
      <Grid container justifyContent="center" style={{ zIndex: 10000 }}>
        <Grid item style={{ maxWidth: 620 }}>
          <GenericMessageModal customMessage={message} hideButton halfSize />
        </Grid>
      </Grid>
    </div>
  );
}
